import { useState, useRef, useCallback, useEffect, type RefObject } from "react";

interface ControllerState {
  lastAngle: number;
  lastTime: number;
  velocity: number;
  moved: boolean;
  totalDelta: number;
  cx: number;
  cy: number;
}

export interface LauncherController {
  rotation: number;
  focusedIdx: number;
  dragging: boolean;
  begin: (clientX: number, clientY: number) => void;
  move: (clientX: number, clientY: number) => void;
  end: () => { moved: boolean };
  reset: () => void;
}

export function useLauncherController(
  centerRef: RefObject<HTMLElement | null>,
  appCount = 11,
  motionPaused = false,
): LauncherController {
  const safeAppCount = Math.max(1, appCount);
  const step = 360 / safeAppCount;
  const [rotation, setRotation] = useState(0);
  const [dragging, setDragging] = useState(false);
  const stateRef = useRef<ControllerState>({
    lastAngle: 0,
    lastTime: 0,
    velocity: 0,
    moved: false,
    totalDelta: 0,
    cx: 0,
    cy: 0,
  });

  const getCenter = useCallback(() => {
    const el = centerRef.current;
    if (!el) return { cx: 0, cy: 0 };
    const r = el.getBoundingClientRect();
    return { cx: r.left + r.width / 2, cy: r.top + r.height / 2 };
  }, [centerRef]);

  const begin = useCallback(
    (clientX: number, clientY: number) => {
      const { cx, cy } = getCenter();
      const ang = (Math.atan2(clientY - cy, clientX - cx) * 180) / Math.PI;
      stateRef.current = {
        lastAngle: ang,
        lastTime: performance.now(),
        velocity: 0,
        moved: false,
        totalDelta: 0,
        cx,
        cy,
      };
      setDragging(true);
    },
    [getCenter],
  );

  const move = useCallback((clientX: number, clientY: number) => {
    const s = stateRef.current;
    const ang = (Math.atan2(clientY - s.cy, clientX - s.cx) * 180) / Math.PI;
    let d = ang - s.lastAngle;
    if (d > 180) d -= 360;
    if (d < -180) d += 360;
    const now = performance.now();
    const dt = Math.max(now - s.lastTime, 1);
    s.lastAngle = ang;
    s.lastTime = now;
    s.velocity = d / dt;
    s.totalDelta += Math.abs(d);
    if (s.totalDelta > 4) s.moved = true;
    setRotation((r) => r - d);
  }, []);

  const end = useCallback(() => {
    setDragging(false);
    if (motionPaused) {
      stateRef.current.velocity = 0;
      setRotation((r) => Math.round(r / step) * step);
    }
    return { moved: stateRef.current.moved };
  }, [motionPaused, step]);

  useEffect(() => {
    if (!motionPaused) return;
    stateRef.current.velocity = 0;
    setDragging(false);
  }, [motionPaused]);

  // Momentum + snap on release
  useEffect(() => {
    if (dragging || motionPaused) return;
    let v = -stateRef.current.velocity * 16;
    v = Math.max(-step * 0.85, Math.min(step * 0.85, v));
    let r = rotation;
    let phase: "momentum" | "snap" = Math.abs(v) > 0.5 ? "momentum" : "snap";
    let raf: number;
    const tick = () => {
      if (phase === "momentum") {
        r += v;
        v *= 0.9;
        if (Math.abs(v) < 0.4) phase = "snap";
      } else {
        const target = Math.round(r / step) * step;
        const diff = target - r;
        r += diff * 0.22;
        if (Math.abs(diff) < 0.04) {
          setRotation(target);
          return;
        }
      }
      setRotation(r);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging, motionPaused, step]);

  const focusedIdx = appCount > 0
    ? ((Math.round(rotation / step) % safeAppCount) + safeAppCount) % safeAppCount
    : -1;
  const reset = useCallback(() => setRotation(0), []);

  return { rotation, focusedIdx, dragging, begin, move, end, reset };
}
